import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiClientManager from 'core/apiClient';
import { getTeamProfileUrl } from 'utils/url';
import { useTenants } from 'TenantModule/hooks/useTenants';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { useSwitchTenantMutation } from 'TenantModule/api/queries/useSwitchTenant';
import { useNotification } from 'hooks/useNotification';
import useAppNavigation from 'hooks/useAppNavigation';

/**
 * Custom hook to handle URL parameters for team navigation with tenant/realm switching
 * Handles URLs: https://galaxy.mosaicapp.com/teams/customers/?team_id=90174&realm_id=prod&tenant_name=prod
 *
 * Process:
 * 1. Extract team_id, realm_id, tenant_name from URL query parameters
 * 2. Find matching tenant and realm objects from global state
 * 3. Switch global realm context using handleSelectGlobalRailsSandbox()
 * 4. Switch global tenant context using switchTenant() and handleSelectGlobalTenant()
 * 5. Navigate to team profile page: /team/{teamId}/all_members?realmId={realmId}&tenant={tenantName}
 *
 * This ensures that API calls on the team profile page use the correct tenant/realm context
 * from the URL parameters, allowing external deep links to work correctly even when
 * the user's current context is different.
 *
 * Example flow:
 * - User clicks link: /teams/customers/?team_id=90174&realm_id=prod&tenant_name=prod
 * - Hook extracts parameters (realm_id and tenant_name from URL)
 * - Hook switches global context to prod realm and prod tenant
 * - Hook navigates to: /team/90174/all_members?realmId=prod&tenant=prod
 *   (note: output URL uses realmId and tenant as query param names)
 * - Team profile page loads with correct prod context
 *
 * @see .github/copilot-instructions.md#api--services API patterns
 * @see src/core/apiClient.ts ApiClientManager.buildUrlHeaders for building headers
 * @see src/utils/url.ts getTeamProfileUrl for URL building
 * @see src/modules/TeamModule/components/TeamsV2Page.tsx Similar tenant/realm switching pattern
 * @see src/hooks/useUrl.tsx URL parameter handling patterns
 *
 * RELATED FILES:
 * - src/core/apiClient.ts - ApiClientManager.buildUrlHeaders() for header building
 * - src/utils/url.ts - getTeamProfileUrl() for building team profile URLs
 * - src/modules/TenantModule/hooks/useTenants.tsx - Tenant management
 * - src/modules/SandboxModule/hooks/useSandboxes.tsx - Realm/sandbox management
 * - src/routes/Routes.tsx - RedirectTeamsToReports() for special handling
 *
 * ⚠️ IF YOU CHANGE THIS HOOK:
 * 1. Verify tenant and realm switching works correctly
 * 2. Ensure team_id, realm_id, tenant_name are passed correctly to team profile page
 * 3. Test with different tenant/realm combinations
 * 4. Update RedirectTeamsToReports in Routes.tsx if URL pattern changes
 */
const useUrlTeamNavigation = () => {
  const navigate = useNavigate();
  const hasProcessedUrl = useRef(false);
  const { showError } = useNotification();

  // Get tenant and realm management hooks
  const { tenantOrder, tenantsHash, handleSelectGlobalTenant } = useTenants({ isOff: false });
  const { getSandbox, handleSelectGlobalRailsSandbox } = useSandboxes();
  const { switchTenant } = useSwitchTenantMutation();
  const { navigateToTeamsV2Table } = useAppNavigation();

  useEffect(() => {
    // Only process URL parameters once
    if (hasProcessedUrl.current) return;

    const queryParams = new URLSearchParams(window.location.search);
    const teamId = queryParams.get('team_id');
    const realmId = queryParams.get('realm_id');
    const tenantName = queryParams.get('tenant_name');

    // If no URL parameters, nothing to do
    if (!teamId || !realmId || !tenantName) return;

    // Wait until tenant data is loaded before processing
    // This prevents race condition where we navigate without switching tenant
    // because tenantOrder/tenantsHash weren't populated yet
    if (
      !tenantOrder ||
      tenantOrder.length === 0 ||
      !tenantsHash ||
      Object.keys(tenantsHash).length === 0
    ) {
      return;
    }

    // Find the tenant and realm objects
    const tenantId = tenantOrder.find((tid) => tenantsHash[tid]?.tenantName === tenantName);
    const tenantObj = tenantId ? tenantsHash[tenantId] : undefined;
    const realmObj = getSandbox(realmId);

    // Helper function to navigate to profile and mark as processed
    const navigateToProfile = () => {
      // Mark as processed only after successful navigation
      hasProcessedUrl.current = true;
      const profileUrl = getTeamProfileUrl({ teamId: +teamId });
      navigate({
        pathname: profileUrl,
        search: `?realmId=${realmId}&tenant=${tenantName}`,
      });
    };

    // Update auth defaults for realm if found
    if (realmObj) {
      handleSelectGlobalRailsSandbox({ sandbox: realmObj });
    }

    // Check if already on the correct tenant (using auth default to avoid deprecation warning)
    const currentTenantName = ApiClientManager.getAuthDefaultTenant()?.tenantName;
    const needsTenantSwitch = tenantObj && currentTenantName !== tenantObj.tenantName;

    // Switch tenant if found and different from current, then navigate
    if (needsTenantSwitch) {
      switchTenant(
        { tenant: tenantObj.tenantName },
        {
          onSuccess: () => {
            handleSelectGlobalTenant({ tenant: tenantObj });
            navigateToProfile();
          },
          onError: () => {
            showError('Failed to switch tenant');
            hasProcessedUrl.current = true;
            navigateToTeamsV2Table();
          },
        },
      );
    } else {
      // If no tenant switching needed (not found or already on correct tenant), navigate directly
      navigateToProfile();
    }
  }, [
    navigate,
    tenantOrder,
    tenantsHash,
    getSandbox,
    handleSelectGlobalRailsSandbox,
    handleSelectGlobalTenant,
    switchTenant,
    showError,
    navigateToTeamsV2Table,
  ]);
};

export default useUrlTeamNavigation;
